/**
 * YouTube Facade Block
 *
 * Replaces the eager YouTube iframe with a lightweight thumbnail + play button.
 * The real iframe (and the YouTube player's JS/CSS/fonts) is only created once
 * the user clicks play, keeping that weight off the initial page load.
 */

function activateYouTubeFacade(facade: HTMLElement): void {
  const embedSrc = facade.dataset.embedSrc;
  if (!embedSrc) return;

  const separator = embedSrc.includes('?') ? '&' : '?';
  const iframe = document.createElement('iframe');
  iframe.src = `${embedSrc}${separator}autoplay=1`;
  iframe.className = 'absolute inset-0 w-full h-full';
  iframe.title = 'YouTube video player';
  iframe.allow = 'accelerometer;autoplay;encrypted-media;gyroscope;picture-in-picture';
  iframe.allowFullscreen = true;
  iframe.referrerPolicy = 'strict-origin-when-cross-origin';
  iframe.frameBorder = '0';

  facade.replaceChildren(iframe);
  iframe.focus();
}

// YouTube serves a 120x90 placeholder (not an error) when maxresdefault doesn't exist for a video.
const YT_PLACEHOLDER_WIDTH = 120;

function applyThumbnailFallback(img: HTMLImageElement): void {
  const fallbackSrc = img.dataset.fallbackSrc;
  if (!fallbackSrc || img.src === fallbackSrc) return;
  img.src = fallbackSrc;
}

function ensureThumbnailQuality(img: HTMLImageElement): void {
  const check = (): void => {
    if (img.naturalWidth <= YT_PLACEHOLDER_WIDTH) applyThumbnailFallback(img);
  };

  if (img.complete) {
    check();
    return;
  }
  img.addEventListener('load', check, { once: true });
  img.addEventListener('error', () => applyThumbnailFallback(img), { once: true });
}

function initYouTubeFacades(): void {
  document.querySelectorAll<HTMLElement>('.youtube-facade').forEach((facade) => {
    const thumb = facade.querySelector<HTMLImageElement>('.youtube-facade__thumb');
    if (thumb) ensureThumbnailQuality(thumb);

    const button = facade.querySelector<HTMLButtonElement>('.youtube-facade__play');
    button?.addEventListener('click', () => activateYouTubeFacade(facade), { once: true });
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initYouTubeFacades);
} else {
  // DOM is already ready
  initYouTubeFacades();
}

export { activateYouTubeFacade, initYouTubeFacades };
